import { useMemo } from 'react';

import useReferralCreditsQuery from 'api/queries/useReferralCreditsQuery';
import { usePromoCodeContext } from 'contexts/PromoCodeContext';
import { getReferralNanoBannerCopy } from 'features/site/header/nanobars/ReferralNanoBanner/utils';
import { getNanobarText } from 'features/site/header/nanobars/SignupNanoBanner/utils';
import useValidReferralsCampaign from 'hooks/useValidReferralsCampaign';
import { punctuateText } from 'utils/globalUtils';

/**
 * Best-effort header nano-banner copy for analytics.
 * Separate properties so referral and promo banners can both be reported when both are visible.
 *
 * @returns {{ promo_banner: string|null, referral_banner: string|null }}
 */
function useNanoBannerAnalyticsProperties() {
    const promoCodeContext = usePromoCodeContext();
    const validReferralsCampaign = useValidReferralsCampaign();
    const { data: referrals = {}, isLoading: isReferralsLoading } = useReferralCreditsQuery();
    const { referralRewards } = referrals ?? {};

    const {
        appliedPromoCodeData,
        customerPromoNanoBanner = {},
        renderSignupNanoBanner = false,
    } = promoCodeContext ?? {};

    const { selectedDiscount, showCustomerPromoNanoBanner } = customerPromoNanoBanner;

    return useMemo(() => {
        let referralBanner = null;
        let promoBanner = null;

        if (validReferralsCampaign) {
            referralBanner = getReferralNanoBannerCopy(referralRewards);
        }

        // Header suppresses customer when referral is live; still report whichever promo surface is up
        if (!validReferralsCampaign && showCustomerPromoNanoBanner && selectedDiscount?.nanobarText) {
            promoBanner = punctuateText(selectedDiscount.nanobarText);
        } else if (renderSignupNanoBanner && !isReferralsLoading) {
            const nanobarText = getNanobarText({ appliedPromoCodeData, referralRewards });
            if (nanobarText) {
                promoBanner = nanobarText;
            }
        }

        return { promo_banner: promoBanner, referral_banner: referralBanner };
    }, [
        appliedPromoCodeData,
        isReferralsLoading,
        referralRewards,
        renderSignupNanoBanner,
        selectedDiscount?.nanobarText,
        showCustomerPromoNanoBanner,
        validReferralsCampaign,
    ]);
}

export default useNanoBannerAnalyticsProperties;
